<template>
  <UmisisModal
    ref="assigneeModal"
    :ok-disabled="isUpdating || !selectedUserId"
    title="Update Default Assignee"
    @ok="submitAssignee"
    @show="onModalShow"
  >
    <div class="flex flex-col gap-4">
      <Field>
        <FieldLabel for="task-template-assignee-user">
          Select user
        </FieldLabel>
        <select
          id="task-template-assignee-user"
          v-model.number="selectedUserId"
          class="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
        >
          <option :value="null" disabled>
            Choose a user
          </option>
          <option v-for="user in users" :key="`task-template-assignee-user-${user.id}`" :value="user.id">
            {{ user.name }} ({{ user.email }})
          </option>
        </select>
        <FieldDescription>Set the default assignee for generated tasks.</FieldDescription>
      </Field>
    </div>
  </UmisisModal>
</template>

<script setup>
import axios from 'axios'
import { computed, ref } from 'vue'
import { toast } from 'vue-sonner'
import UmisisModal from '../../../../../../../../resources/js/components/umisis/generic/UmisisModal.vue'
import { useModalControl } from '../../../../../../../../resources/js/composables/modal/useUmisisModal.js'
import { Field, FieldDescription, FieldLabel } from '../../../../../../../../resources/js/components/ui/field/index.js'
import { useTaskTemplateStore } from '../../../stores/taskTemplateStore.js'

const sharedUsers = ref([])
const loadingUsers = ref(false)
const hasLoadedUsers = ref(false)

const props = defineProps({
  taskTemplateId: {
    type: Number,
    required: true,
  },
  assigneeId: {
    type: [Number, String, Object],
    default: null,
  },
  context: {
    type: String,
    default: 'task-template-show',
  },
})

const emit = defineEmits(['loaded-users'])

const selectedUserId = ref(null)
const { componentRef: assigneeModal, openModal, closeModal } = useModalControl()

const taskTemplateStore = useTaskTemplateStore(props.context)
const users = computed(() => sharedUsers.value)
const normalizedAssigneeId = computed(() => {
  const parsedId = Number(props.assigneeId)

  if (!Number.isInteger(parsedId) || parsedId <= 0) {
    return null
  }

  return parsedId
})
const isUpdating = computed(() => {
  if (!Number.isInteger(props.taskTemplateId) || props.taskTemplateId <= 0) {
    return false
  }

  return taskTemplateStore.isUpdatingAssignee(props.taskTemplateId)
})

const emitUsers = () => {
  emit('loaded-users', users.value)
}

const fetchUsers = async () => {
  if (hasLoadedUsers.value || loadingUsers.value) {
    emitUsers()
    return
  }

  loadingUsers.value = true

  try {
    const { data } = await axios.get('/api/v1/users', {
      params: {
        not_paginated: true,
        order_by: 'name',
        order_direction: 'asc',
      },
    })

    sharedUsers.value = Array.isArray(data?.data) ? data.data : (Array.isArray(data) ? data : [])
    hasLoadedUsers.value = true
    emitUsers()
  } catch (error) {
    const errorMessage = error?.response?.data?.message || 'Unable to load users.'
    toast.error(errorMessage)
  } finally {
    loadingUsers.value = false
  }
}

const onModalShow = async () => {
  selectedUserId.value = normalizedAssigneeId.value
  await fetchUsers()
}

const submitAssignee = async (event) => {
  event?.preventDefault?.()

  if (!Number.isInteger(props.taskTemplateId) || props.taskTemplateId <= 0) {
    return
  }

  if (!Number.isInteger(selectedUserId.value) || selectedUserId.value <= 0) {
    toast.error('Select a valid user.')
    return
  }

  if (selectedUserId.value === normalizedAssigneeId.value) {
    closeModal()
    return
  }

  try {
    const updatedTaskTemplate = await taskTemplateStore.updateAssignee(props.taskTemplateId, selectedUserId.value)
    const selectedUser = users.value.find((user) => user.id === Number(updatedTaskTemplate?.assigned_to_user_id))
    toast.success(selectedUser ? `Default assignee updated to ${selectedUser.name}.` : 'Default assignee updated.')
    closeModal()
  } catch (error) {
    const errorMessage = error?.response?.data?.message
      || error?.response?.data?.errors?.assigned_to_user_id?.[0]
      || 'Unable to update default assignee.'

    toast.error(errorMessage)
  }
}

defineExpose({
  openModal,
})
</script>
